import { LeftOutlined, RightOutlined, StarFilled, UserOutlined } from '@ant-design/icons'
import { Image } from 'antd'
import { useEffect, useState } from 'react'
import Config from '../../../config'
import { getTopHosts, TopHost, TopHostReview } from '../../../services/hosts'
import { PageLang } from '../pageLang'

const CONTENT: Record<PageLang, { eyebrow: string; headline: string; viewDetails: string }> = {
  EN: {
    eyebrow: 'Partners community',
    headline: 'Browse how others are earning with their cars.',
    viewDetails: 'View details',
  },
  TH: {
    eyebrow: 'ชุมชนพาร์ทเนอร์',
    headline: 'ดูตัวอย่างว่าเจ้าของรถคนอื่นสร้างรายได้จากรถของพวกเขาอย่างไร',
    viewDetails: 'ดูรายละเอียด',
  },
}

const HOSTS_PER_PAGE = 2

const skeletonStyles = `
  @keyframes testimonials-skeleton-pulse {
    0% { opacity: 0.6; }
    50% { opacity: 1; }
    100% { opacity: 0.6; }
  }
  .testimonials-skeleton {
    background-color: #E4E7EC;
    border-radius: 4px;
    animation: testimonials-skeleton-pulse 1.2s ease-in-out infinite;
  }
`

const SkeletonReviewCard = () => (
  <div
    style={{
      flex: '0 0 220px',
      backgroundColor: '#F4F5F9',
      borderRadius: '10px',
      padding: '12px',
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
      <div
        className="testimonials-skeleton"
        style={{ width: '26px', height: '26px', borderRadius: '50%' }}
      />
      <div className="testimonials-skeleton" style={{ width: '70px', height: '10px' }} />
    </div>
    <div
      className="testimonials-skeleton"
      style={{ width: '90px', height: '10px', marginBottom: '8px' }}
    />
    <div
      className="testimonials-skeleton"
      style={{ width: '100%', height: '10px', marginBottom: '4px' }}
    />
    <div className="testimonials-skeleton" style={{ width: '80%', height: '10px' }} />
  </div>
)

const SkeletonHostCard = () => (
  <div
    style={{
      border: '1px solid #E4E7EC',
      borderRadius: '14px',
      padding: '14px',
      marginBottom: '12px',
    }}
  >
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
      <div
        className="testimonials-skeleton"
        style={{ width: '44px', height: '44px', borderRadius: '50%', flexShrink: 0 }}
      />
      <div style={{ flex: 1 }}>
        <div
          className="testimonials-skeleton"
          style={{ width: '120px', height: '12px', marginBottom: '8px' }}
        />
        <div
          className="testimonials-skeleton"
          style={{ width: '160px', height: '10px', marginBottom: '8px' }}
        />
        <div className="testimonials-skeleton" style={{ width: '100px', height: '10px' }} />
      </div>
    </div>
    <div style={{ borderTop: '1px solid #EEF0F3', margin: '12px 0' }} />
    <div style={{ display: 'flex', gap: '8px' }}>
      <SkeletonReviewCard />
      <SkeletonReviewCard />
    </div>
  </div>
)

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return ''
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[1][0]).toUpperCase()
}

const HostAvatar = ({ host }: { host: TopHost }) =>
  host.avatarUrl ? (
    <img
      src={host.avatarUrl}
      alt={host.name}
      style={{ width: '44px', height: '44px', borderRadius: '50%', objectFit: 'cover' }}
    />
  ) : (
    <div
      style={{
        width: '44px',
        height: '44px',
        borderRadius: '50%',
        backgroundColor: '#0048B3',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '14px',
        fontWeight: 600,
        flexShrink: 0,
      }}
    >
      {getInitials(host.name)}
    </div>
  )

const ReviewCard = ({ review }: { review: TopHostReview }) => (
  <div
    style={{
      flex: '0 0 220px',
      backgroundColor: '#F4F5F9',
      borderRadius: '10px',
      padding: '12px',
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
      <div
        style={{
          width: '26px',
          height: '26px',
          borderRadius: '50%',
          backgroundColor: '#E4E7EC',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <UserOutlined style={{ color: '#8C97A7', fontSize: '13px' }} />
      </div>
      <div
        style={{
          fontSize: '12.5px',
          fontWeight: 600,
          color: '#212427',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {review.reviewerName}
      </div>
    </div>
    <div style={{ marginBottom: '6px' }}>
      {Array.from({ length: 5 }).map((_, i) => (
        <StarFilled
          key={i}
          style={{
            color: i < Math.round(review.rating) ? '#F5A623' : '#E4E7EC',
            fontSize: '11px',
            marginRight: '2px',
          }}
        />
      ))}
    </div>
    {review.text && (
      <p
        style={{
          fontSize: '12px',
          color: '#667085',
          lineHeight: '1.5',
          margin: '0 0 8px',
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {review.text}
      </p>
    )}
    {review.pictureList.length > 0 && (
      <Image.PreviewGroup>
        <div style={{ display: 'flex', gap: '4px' }}>
          {review.pictureList.slice(0, 3).map((url, i) => (
            <Image
              key={i}
              src={url}
              alt=""
              width={40}
              height={40}
              style={{ borderRadius: '6px', objectFit: 'cover', flexShrink: 0, cursor: 'pointer' }}
            />
          ))}
        </div>
      </Image.PreviewGroup>
    )}
  </div>
)

const HostCard = ({ host, t }: { host: TopHost; t: typeof CONTENT['EN'] }) => (
  <div
    style={{
      border: '1px solid #E4E7EC',
      borderRadius: '14px',
      padding: '14px',
      marginBottom: '12px',
    }}
  >
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
      <HostAvatar host={host} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '14px', fontWeight: 600, color: '#212427' }}>{host.name}</div>
        <div
          style={{
            fontSize: '12px',
            color: '#8C97A7',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {[host.vehicleDisplay, host.city].filter(Boolean).join(' · ')}
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            fontSize: '12px',
            color: '#667085',
            marginTop: '2px',
          }}
        >
          <StarFilled style={{ color: '#F5A623', fontSize: '11px' }} />
          <span style={{ fontWeight: 600, color: '#212427' }}>{host.rating}</span>
          <span>({host.reviewCount})</span>
        </div>
      </div>
      <a
        href={`${Config.webReserve.URL}/car-rental/host/${host.hostCode}`}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          fontSize: '12.5px',
          fontWeight: 600,
          color: '#0048B3',
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}
      >
        {t.viewDetails} →
      </a>
    </div>

    {host.reviews.length > 0 && (
      <>
        <div style={{ borderTop: '1px solid #EEF0F3', margin: '12px 0' }} />
        <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '2px' }}>
          {host.reviews.map((review, i) => (
            <ReviewCard key={i} review={review} />
          ))}
        </div>
      </>
    )}
  </div>
)

export interface TestimonialsSectionProps {
  lang: PageLang
}

export const TestimonialsSection = ({ lang }: TestimonialsSectionProps) => {
  const t = CONTENT[lang]
  const [hosts, setHosts] = useState<TopHost[]>([])
  const [pageIndex, setPageIndex] = useState(0)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    getTopHosts()
      .then(setHosts)
      .catch(() => setHosts([]))
      .finally(() => setLoading(false))
  }, [])

  if (!loading && hosts.length === 0) return null

  const totalPages = Math.ceil(hosts.length / HOSTS_PER_PAGE)
  const currentHosts = hosts.slice(
    pageIndex * HOSTS_PER_PAGE,
    pageIndex * HOSTS_PER_PAGE + HOSTS_PER_PAGE,
  )
  const canGoPrev = pageIndex > 0
  const canGoNext = pageIndex < totalPages - 1

  return (
    <div style={{ padding: '32px 16px 8px' }}>
      <style>{skeletonStyles}</style>
      <div
        style={{
          display: 'inline-block',
          backgroundColor: '#EAF1FF',
          color: '#0048B3',
          fontSize: '12px',
          fontWeight: 600,
          padding: '4px 12px',
          borderRadius: '12px',
          marginBottom: '12px',
        }}
      >
        {t.eyebrow}
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: '12px',
          marginBottom: '18px',
        }}
      >
        <h2 style={{ fontSize: '20px', fontWeight: 600, color: '#212427', margin: 0, flex: 1 }}>
          {t.headline}
        </h2>
        <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
          <button
            onClick={() => canGoPrev && setPageIndex((p) => p - 1)}
            disabled={!canGoPrev || loading}
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              border: 'none',
              backgroundColor: canGoPrev && !loading ? '#E4E7EC' : '#F4F5F9',
              color: canGoPrev && !loading ? '#212427' : '#C7CCD3',
              cursor: canGoPrev && !loading ? 'pointer' : 'default',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <LeftOutlined />
          </button>
          <button
            onClick={() => canGoNext && setPageIndex((p) => p + 1)}
            disabled={!canGoNext || loading}
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              border: 'none',
              backgroundColor: canGoNext && !loading ? '#0048B3' : '#F4F5F9',
              color: canGoNext && !loading ? '#fff' : '#C7CCD3',
              cursor: canGoNext && !loading ? 'pointer' : 'default',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <RightOutlined />
          </button>
        </div>
      </div>

      {loading ? (
        <>
          <SkeletonHostCard />
          <SkeletonHostCard />
        </>
      ) : (
        currentHosts.map((host) => <HostCard key={host.hostUserId} host={host} t={t} />)
      )}
    </div>
  )
}

export default TestimonialsSection
